import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthModal from "@/components/feature/AuthModal";
import Logo from "@/components/feature/Logo";
import NotificationBell from "@/components/feature/NotificationBell";
import { useAuth } from "@/contexts/AuthContext";
import { useActivityLog } from "@/hooks/useActivityLog";

export default function Header() {
  const { user, profile, signOut } = useAuth();
  const { logActivity } = useActivityLog();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    const handleAuthEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setAuthMode(detail.mode || "login");
      setAuthModalOpen(true);
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("open-auth-modal", handleAuthEvent);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("open-auth-modal", handleAuthEvent);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logActivity("logout", { timestamp: new Date().toISOString() });
    await signOut();
    setUserMenuOpen(false);
  };

  const navLinks = [
    { label: "Home", to: "/", isHash: false },
    { label: "Hacking News", to: "/hacking-news", isHash: false },
    { label: "Hackers", to: "/hackers", isHash: false },
    { label: "Recent Projects", to: "/projects-listing", isHash: false },
    { label: "Contact", to: "/contact", isHash: false },
  ];

  const handleNavClick = (to: string) => {
    setMobileMenuOpen(false);
    navigate(to);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileMenuOpen
            ? "bg-[#0d1117]/95 shadow-md"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Logo variant="dark" />

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`text-xs font-semibold tracking-wider transition-colors hover:opacity-80 whitespace-nowrap ${
                    scrolled || mobileMenuOpen ? "text-gray-300" : "text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Auth buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/post-project"
                className={`text-xs font-semibold px-4 py-2 rounded border transition-all whitespace-nowrap ${
                  scrolled || mobileMenuOpen
                    ? "border-gray-600 text-gray-300 hover:bg-[#1c2128]"
                    : "border-white/50 text-white hover:bg-[#0d1117]/10"
                }`}
              >
                <i className="fas fa-plus mr-1" />Post Project
              </Link>

              {user && (
                <NotificationBell textClassName={scrolled || mobileMenuOpen ? "text-gray-300" : "text-white"} />
              )}

              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className={`flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded transition-all whitespace-nowrap ${
                      scrolled || mobileMenuOpen
                        ? "text-gray-300 hover:bg-[#21262d]"
                        : "text-white hover:bg-[#0d1117]/10"
                    }`}
                  >
                    <div className="w-6 h-6 rounded-full bg-[#5cb85c] flex items-center justify-center text-white text-xs font-bold">
                      {(profile?.username || profile?.display_name || user.email || "U").charAt(0).toUpperCase()}
                    </div>
                    <span>{profile?.username || profile?.display_name || user.email || "User"}</span>
                    <i className={`fas fa-chevron-down text-[10px] transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                  </button>
                  {userMenuOpen && (
                    <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg py-1 border ${
                      scrolled || mobileMenuOpen ? "bg-[#0d1117] border-gray-700" : "bg-[#2c3e50] border-gray-700"
                    }`}>
                      <div className={`px-4 py-2 text-xs border-b ${scrolled || mobileMenuOpen ? "text-gray-400 border-gray-800" : "text-gray-400 border-gray-700"}`}>
                        {user.email}
                      </div>
                      {(profile?.role === "hacker" || profile?.role === "admin") && (
                        <Link
                          to="/dashboard"
                          className={`block px-4 py-2 text-xs font-semibold transition-colors ${
                            scrolled || mobileMenuOpen ? "text-gray-300 hover:bg-[#1c2128]" : "text-white hover:bg-[#0d1117]/10"
                          }`}
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <i className="fas fa-tachometer-alt mr-2" />Dashboard
                        </Link>
                      )}
                      <Link
                        to="/post-project"
                        className={`block px-4 py-2 text-xs font-semibold transition-colors hover:bg-[#21262d] ${
                          scrolled || mobileMenuOpen ? "text-gray-300 hover:bg-[#1c2128]" : "text-white hover:bg-[#0d1117]/10"
                        }`}
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <i className="fas fa-plus mr-2" />Post a Project
                      </Link>
                      <button
                        onClick={handleLogout}
                        className={`w-full text-left px-4 py-2 text-xs font-semibold transition-colors ${
                          scrolled || mobileMenuOpen ? "text-red-400 hover:bg-red-900/20" : "text-red-400 hover:bg-red-900/20"
                        }`}
                      >
                        <i className="fas fa-sign-out-alt mr-2" />Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    onClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
                    className={`text-xs font-semibold px-4 py-2 rounded transition-all whitespace-nowrap ${
                      scrolled || mobileMenuOpen
                        ? "text-gray-300 hover:text-gray-100"
                        : "text-white hover:text-white/80"
                    }`}
                  >
                    Login
                  </button>
                  <button
                    onClick={() => { setAuthMode("signup"); setAuthModalOpen(true); }}
                    className="text-xs font-semibold px-4 py-2 rounded bg-[#5cb85c] text-white hover:bg-[#4cae4c] transition-all whitespace-nowrap"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 z-[60] relative"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <i
                className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"} text-lg ${
                  scrolled || mobileMenuOpen ? "text-gray-200" : "text-white"
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setMobileMenuOpen(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" />

          {/* Drawer */}
          <div
            className="absolute top-0 right-0 w-[280px] max-w-[85vw] h-full bg-[#0d1117] shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header area */}
            <div className="flex items-center justify-between px-5 h-16 border-b border-gray-800 shrink-0">
              <span className="text-sm font-semibold text-gray-400">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-800 transition-colors"
                aria-label="Close menu"
              >
                <i className="fas fa-times text-gray-300" />
              </button>
            </div>

            {/* Nav Links */}
            <nav className="flex-1 overflow-y-auto py-4">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.to)}
                  className="w-full text-left px-5 py-4 text-sm font-semibold text-gray-300 border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Bottom Actions */}
            <div className="shrink-0 border-t border-gray-800 p-5 space-y-3">
              <button
                onClick={() => { setMobileMenuOpen(false); navigate("/post-project"); }}
                className="w-full text-center text-sm font-semibold px-4 py-3 rounded border border-[#5cb85c] text-[#5cb85c] hover:bg-[#5cb85c]/10 transition-colors"
              >
                <i className="fas fa-plus mr-2" />Post a Project
              </button>

              {user && (
                <div className="flex justify-center py-1">
                  <NotificationBell textClassName="text-gray-300" />
                </div>
              )}

              {user ? (
                <>
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate("/dashboard"); }}
                    className="w-full text-center text-sm font-semibold px-4 py-3 rounded border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors"
                  >
                    <i className="fas fa-tachometer-alt mr-2" />Dashboard
                  </button>
                  <button
                    onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                    className="w-full text-center text-sm font-semibold px-4 py-3 rounded border border-red-700/50 text-red-400 hover:bg-red-900/20 transition-colors"
                  >
                    <i className="fas fa-sign-out-alt mr-2" />Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => { setMobileMenuOpen(false); setAuthMode("login"); setAuthModalOpen(true); }}
                    className="flex-1 text-center text-sm font-semibold px-4 py-3 rounded border border-gray-600 text-gray-300 hover:bg-gray-800 transition-colors"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => { setMobileMenuOpen(false); setAuthMode("signup"); setAuthModalOpen(true); }}
                    className="flex-1 text-center text-sm font-semibold px-4 py-3 rounded bg-[#5cb85c] text-white hover:bg-[#4cae4c] transition-colors"
                  >
                    Sign Up
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode={authMode} />
    </>
  );
}